import { useEffect, useState } from 'react';
import { rescheduleActionLabel, rescheduleStatusLabel } from '../../variables/rescheduleActionLabels';
import * as rescheduleApi from './rescheduleApi';
import wsManager from '../../services/wsManager';
import IconCloseX from '../../components/icons/IconCloseX';
import '../../css/modals.css';
import '../../css/reschedule.css';

// Read-only "has this lesson ever been rescheduled or had a cancel request"
// view - separate from RescheduleResponseModal.jsx (which is for ACTING on
// a currently-pending request). A lesson can accumulate several resolved
// reschedule threads over its lifetime (declined/accepted requests don't
// block a later new one, only an ACTIVE one does) - each thread renders as
// its own block with its own status + history, rather than flattening
// everything into one interleaved timeline (simpler to build correctly).
export default function LessonNegotiationHistoryPopup({ isOpen, lessonId, onClose }) {
   const [status, setStatus] = useState('loading');
   const [reschedules, setReschedules] = useState([]);
   const [cancels, setCancels] = useState([]);

   const load = () => {
      if (!isOpen || !lessonId) return;
      setStatus('loading');
      Promise.all([
         rescheduleApi.getLessonRescheduleRequests(lessonId).catch(() => ({ data: [] })),
         rescheduleApi.getLessonCancelRequests(lessonId).catch(() => ({ data: [] })),
      ]).then(([rescheduleRes, cancelRes]) => {
         const rawReschedules = Array.isArray(rescheduleRes.data) ? rescheduleRes.data : [];
         const rawCancels = Array.isArray(cancelRes.data) ? cancelRes.data : [];
         setCancels(rawCancels);
         return Promise.all(rawReschedules.map((r) => rescheduleApi.getRescheduleDetail(r.id).then((res) => res.data).catch(() => null)));
      }).then((details) => {
         setReschedules((details || []).filter(Boolean));
         setStatus('done');
      }).catch(() => setStatus('error'));
   };

   useEffect(load, [isOpen, lessonId]);

   // Same live-refresh approach as RescheduleResponseModal.jsx/
   // LessonDetailModal.jsx - lessonId is already a prop here (unlike those
   // two, no need to wait for a first fetch to learn it).
   useEffect(() => {
      if (!isOpen || !lessonId) return;
      const topic = '/topic/reschedule/' + lessonId;
      const handler = () => load();
      wsManager.subscribe(topic, handler);
      return () => wsManager.unsubscribe(topic, handler);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isOpen, lessonId]);

   if (!isOpen) return '';

   const stopProp = (e) => e.stopPropagation();
   const isEmpty = status === 'done' && reschedules.length === 0 && cancels.length === 0;

   return (
      <div className='modal-update' onClick={onClose} style={{ display: 'flex' }}>
         <div className='modal-window reschedule-modal-window' onClick={stopProp}>
            <div className='reschedule-modal-header'>
               <h3 className='modal-w-title'>Історія перенесень і скасувань</h3>
               <div className='reschedule-modal-close' onClick={onClose}><IconCloseX style={{ width: 16, height: 16 }} /></div>
            </div>

            {status === 'loading' ? <p className='txt-c'>Завантаження...</p> : ''}
            {status === 'error' ? <p className='platform-modal-error'>Не вдалося завантажити історію</p> : ''}
            {isEmpty ? <p className='student-platform-empty-hint'>Це заняття ще ніколи не переносили і не скасовували</p> : ''}

            {reschedules.map((d) => (
               <div key={'r' + d.request.id} className='reschedule-history-thread'>
                  <div className='reschedule-modal-lesson-info'>
                     <span className='reschedule-modal-lesson-date'>Перенесення</span>
                     <span className={'reschedule-modal-status-pill status-' + d.request.status}>{rescheduleStatusLabel(d.request.status)}</span>
                  </div>
                  <div className='reschedule-history-list'>
                     {(d.history || []).map((h) => (
                        <div key={h.id} className={'reschedule-history-row actor-' + h.actorType}>
                           <span className='reschedule-history-actor'>{h.actorName || (h.actorType === 'TEACHER' ? 'Вчитель' : 'Учень')}</span>
                           <span className='reschedule-history-action'>{rescheduleActionLabel(h.action)}</span>
                           {h.message ? <span className='reschedule-history-message'>«{h.message}»</span> : ''}
                           <span className='reschedule-history-date'>{h.dAdd ? h.dAdd.replace('T', ' ').slice(0, 16) : ''}</span>
                        </div>
                     ))}
                  </div>
               </div>
            ))}

            {cancels.map((c) => (
               <div key={'c' + c.id} className='reschedule-history-thread'>
                  <div className='reschedule-modal-lesson-info'>
                     <span className='reschedule-modal-lesson-date'>Скасування - {c.student ? (c.student.name + ' ' + c.student.surname) : ''}</span>
                     <span className={'reschedule-modal-status-pill status-' + c.status}>{c.status === 'PENDING' ? 'Очікує' : c.status === 'APPROVED' ? 'Схвалено' : 'Відхилено'}</span>
                  </div>
                  <div className='reschedule-history-list'>
                     <div className='reschedule-history-row actor-STUDENT'>
                        <span className='reschedule-history-actor'>{c.student ? (c.student.name + ' ' + c.student.surname) : 'Учень'}</span>
                        <span className='reschedule-history-action'>Попросив(ла) скасувати</span>
                        {c.message ? <span className='reschedule-history-message'>«{c.message}»</span> : ''}
                        <span className='reschedule-history-date'>{c.dAdd ? c.dAdd.replace('T', ' ').slice(0, 16) : ''}</span>
                     </div>
                     {c.status !== 'PENDING' ? (
                        <div className='reschedule-history-row actor-TEACHER'>
                           <span className='reschedule-history-actor'>Вчитель</span>
                           <span className='reschedule-history-action'>{c.status === 'APPROVED' ? 'Підтвердив(ла) скасування' : 'Відхилив(ла) запит'}</span>
                           {c.teacherMessage ? <span className='reschedule-history-message'>«{c.teacherMessage}»</span> : ''}
                           <span className='reschedule-history-date'>{c.resolvedAt ? c.resolvedAt.replace('T', ' ').slice(0, 16) : ''}</span>
                        </div>
                     ) : ''}
                  </div>
               </div>
            ))}
         </div>
      </div>
   );
}
